import { inject as service } from '@ember/service';
import { isNone, isPresent } from '@ember/utils';
import { dropTask, task } from 'ember-concurrency';
import { Resource } from 'ember-modify-based-class-resource';
import moment from 'moment';
import { task as trackedTask } from 'reactiveweb/ember-concurrency';

import { computeTimeSlotDiffInMins } from 'calendar/utils/time-slots';
import { NUM_BTTP_SLOTS, LEGACY_BTTP_VERSION } from 'later/utils/constants';
import { fetch } from 'later/utils/fetch';

import type Store from '@ember-data/store';
import type { LegacyBttpInstance, ImmediateBttpInstance, BttpInstance, BttpReport } from 'calendar/types/bttp';
import type IntlService from 'ember-intl/services/intl';
import type AnalyticsReportModel from 'later/models/analytics-report';
import type SocialProfileModel from 'later/models/social-profile';
import type TimeSlotModel from 'later/models/time-slot';
import type BttpService from 'later/services/calendar/bttp';
import type SelectedSocialProfilesService from 'later/services/selected-social-profiles';
import type { UntypedService } from 'shared/types';

// Type guard for LegacyBttpInstance
function isLegacyBttp(_bttp: BttpInstance, isLegacy: boolean): _bttp is LegacyBttpInstance {
  return isLegacy;
}

const ERROR_MESSAGES = {
  MISSING_METRICS: 'Missing metrics',
  NOT_ENOUGH_POSTS: 'Not enough posts to run',
  TIMESLOT_ERROR: 'Error fetching best timeslots'
};

const ERROR_CODES = {
  DATA_MALFORMED: 'input_data_malformed',
  MISSING_METRICS: 'missing_metrics',
  NOT_ENOUGH_POSTS: 'not_enough_posts'
};

export default class BttpResource extends Resource {
  @service('calendar/bttp') declare bttp: BttpService;
  @service declare errors: UntypedService;
  @service declare intl: IntlService;
  @service declare selectedSocialProfiles: SelectedSocialProfilesService;
  @service declare store: Store;

  reports: Record<SocialProfileModel['id'], BttpReport> = {};

  get validSelectedProfiles(): SocialProfileModel[] {
    return this.selectedSocialProfiles.profiles.filter((profile) => this.bttp.couldShow(profile.profileType));
  }

  #generateReportErrorText(reportData: BttpReport): string | undefined {
    const { errorCode } = reportData;
    // Note: typed `string`, but `_requestData`'s catch can stash a raw Error here.
    const rawError: unknown = reportData.error;
    const errorText = typeof rawError === 'string' ? rawError : '';
    const errorMessage = rawError instanceof Error ? rawError.message : errorText;

    if (errorCode === ERROR_CODES.NOT_ENOUGH_POSTS || errorMessage.includes(ERROR_MESSAGES.NOT_ENOUGH_POSTS)) {
      return this.intl.t('bttp.errors.not_enough_posts_details');
    }

    if (isPresent(errorText)) {
      return errorText;
    }

    // Note: non-string errors are internal exceptions (e.g. JSON.parse SyntaxError) — don't leak raw text.
    if (isPresent(rawError)) {
      return this.intl.t('bttp.errors.unable_to_fetch');
    }

    return errorCode;
  }

  #handleReportErrors(reportData: BttpReport, report: AnalyticsReportModel): string | void {
    if (!reportData || reportData.error) {
      const errorMessage = this.intl.t('bttp.errors.unable_to_fetch');
      return this.errors.log(errorMessage, report);
    }
  }

  _requestData = task(async (socialProfile: SocialProfileModel, report: AnalyticsReportModel) => {
    if (isNone(report.url)) {
      return { error: this.intl.t('bttp.errors.missing_s3_url') };
    }

    try {
      const url = report.s3SignedUrl;
      const rawData = await fetch(url);
      const parsedData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      const isLegacy = parsedData.version === LEGACY_BTTP_VERSION;
      const defaultTimeZone = 'America/Vancouver';

      const baseTimeSlot = {
        socialProfile,
        slotType: 'bttp',
        isBttp: true,
        isUser: false,
        isDeleted: false,
        profileColorClass: socialProfile.profileColorClass
      };

      const timeSlots = (parsedData.data?.bttp_data ?? []).map(
        (bttp: LegacyBttpInstance | (ImmediateBttpInstance & { minute?: number })) => {
          if (isLegacyBttp(bttp, isLegacy)) {
            const { score, val } = bttp;
            const MINUTES_PER_HOUR = 60;
            const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
            const wday = Math.floor(val / MINUTES_PER_DAY);
            const minuteOfDay = val % MINUTES_PER_DAY;
            const hour = Math.floor(minuteOfDay / MINUTES_PER_HOUR);
            const minute = minuteOfDay % MINUTES_PER_HOUR;
            return {
              ...baseTimeSlot,
              wday,
              hour,
              minute,
              score: score ?? 0
            };
          }
          const { wday, hour, minute } = bttp;
          const utcDateTime = moment
            .tz(defaultTimeZone)
            .day(wday)
            .hour(hour)
            .minute(minute || 0)
            .utc();
          return {
            ...baseTimeSlot,
            wday: utcDateTime.day(),
            hour: utcDateTime.hour(),
            minute: utcDateTime.minute(),
            score: 0
          };
        }
      );

      return { ...parsedData, timeSlots };
    } catch (error) {
      if ((error as Error & { code?: number }).code !== 404) {
        this.errors.handleAjax(error);
      }
      return { timeSlots: [], error };
    }
  });

  #filterLegacyBttpTimeSlotsFromList(timeSlots: TimeSlotModel[]): TimeSlotModel[] {
    const minDist = 60 * 4;
    const rawSorted = [...timeSlots].sort((a, b) => b.score! - a.score!);

    const filtered = rawSorted.reduce((accumulator: TimeSlotModel[], timeSlot: TimeSlotModel) => {
      const isInvalid = accumulator.some((other) => computeTimeSlotDiffInMins(timeSlot, other) < minDist);
      if (!isInvalid) accumulator.push(timeSlot);
      return accumulator;
    }, []);

    return filtered.slice(0, NUM_BTTP_SLOTS);
  }

  _fetchBttpReportForProfile = task(async (socialProfile: SocialProfileModel) => {
    const bttpReportUnavailable = !this.bttp.couldShow(socialProfile.profileType);

    if (bttpReportUnavailable || !socialProfile.bttpReportName) return {};

    const reports = await this.store.query('analytics-report', {
      social_profile_id: socialProfile.id,
      names: [socialProfile.bttpReportName]
    });

    const report = reports.firstObject;

    if (!report) return {};

    const reportData = await this._requestData.perform(socialProfile, report);

    this.#handleReportErrors(reportData, report);

    const isLegacy = reportData?.version === LEGACY_BTTP_VERSION;
    const timeSlots = isLegacy ? this.#filterLegacyBttpTimeSlotsFromList(reportData.timeSlots) : reportData.timeSlots;

    const bttpReport = {
      ...reportData,
      numOfFollowers: socialProfile.followedBy,
      numOfPosts: reportData?.error_data?.data?.num_of_posts,
      reportErrorText: reportData ? this.#generateReportErrorText(reportData) : undefined,
      requiredBttpPostData: 50,
      requiredFollowerCount: 100,
      timeSlots
    };

    this.reports[socialProfile.id] = bttpReport;
    socialProfile.bttpReport = bttpReport;

    return bttpReport;
  });

  _fetchBttpReports = dropTask(async () => {
    if (!this.validSelectedProfiles.length) return;

    await Promise.all(
      this.validSelectedProfiles
        // Filter out reports that have already been fetched
        .filter((p) => !this.reports[p.id])
        .map((p) => this._fetchBttpReportForProfile.perform(p))
    );

    return this.reports;
  });

  fetchTask = trackedTask(this, this._fetchBttpReports, () => [this.selectedSocialProfiles.profileIds.join(',')]);
}
